import React from "react";
import _ from "lodash";
// @ts-expect-error TS7016 Adopt TS
import { gzip } from "pako/lib/deflate";
import StackTrace from "stacktrace-js";

import Sentry, { initSentry as initLiftoffSentry } from "@liftoff/sentry/react";

export const production = process.env.NODE_ENV === "production";
// staging is for running production builds on staging environments
export const staging = !!process.env.STAGING_ENV;

console.log("Production", production);
if (staging) {
  console.log("Staging", staging);
}

// The ploy version.
export const appVersion = process.env.APP_VERSION;

console.debug("App version", appVersion);

// deployedVersion can be falsy if ploy server is not reachable
// @ts-expect-error TS7006 Adopt TS
export function isOutdatedAppVersion(deployedVersion) {
  const outdated =
    appVersion && deployedVersion && appVersion !== deployedVersion;
  if (outdated) console.debug("New app version available", deployedVersion);
  return outdated;
}

// @ts-expect-error TS7006 Adopt TS
export function capitalizeFirst(str) {
  if (!str || str.length === 0) return str;
  // biome-ignore lint/style/noSubstr: Biome linting adoption
  return str[0].toUpperCase() + str.substring(1);
}

// @ts-expect-error TS7006 Adopt TS
export const humanizeSnakeCase = (string) => {
  if (!string) return string;
  return string.split("_").map(_.capitalize).join(" ");
};

// @ts-expect-error TS7006 Adopt TS
export const humanizeCamelCase = (s) => {
  return _.capitalize(s.replace(/([A-Z])/g, " $1"));
};

// @ts-expect-error TS7006 Adopt TS
export function isNullOrUndefined(val) {
  return _.isUndefined(val) || _.isNull(val);
}

// @ts-expect-error TS7006 Adopt TS
export function isDefined(val) {
  return !(_.isUndefined(val) || _.isNull(val));
}

// @ts-expect-error TS7006 Adopt TS
export function nullifyEmptyArray(arr) {
  return _.isEmpty(arr) ? null : arr;
}

// @ts-expect-error TS7006 Adopt TS
export function checkResponse(response) {
  if (!response.ok) {
    if (response.status === 401) {
      // @ts-expect-error TS2322 Adopt TS
      window.location = "/signout";
      // TODO(nathan): This promise remains unresolved until execution ends from navigating to /signout.
      // This is because the fetch requests that use checkResponse do not catch promise rejections
      // and would report to /js_error, and there is no common 'empty' state to resolve to.
      return new Promise(_.noop);
    }
    if (response.status === 400 || response.status === 403) {
      // NOTE(stefan): We return unauthorized requests in case it contains a JSON payload.
      return response;
    }
    return response.text().then(() => {
      throw new Error(
        `Request unsuccessful: ${response.url} ${response.status} ${response.statusText}`
      );
    });
  }
  return undefined;
}

// @ts-expect-error TS7006 Adopt TS
export function responseToText(response) {
  return checkResponse(response) || response.text();
}

/**
 * Pass through 2xx responses. On 400/403, reject with the response body so the
 * caller's failure handler surfaces the server message verbatim. Other non-2xx
 * statuses delegate to checkResponse (401 sign-out, everything else rejects).
 */
export function throwClientErrors(response: Response): Promise<Response> {
  if (response.ok) {
    return Promise.resolve(response);
  }
  if (response.status === 400 || response.status === 403) {
    return response
      .text()
      .then((message) =>
        Promise.reject(
          message || `Request unsuccessful: ${response.url} ${response.status}`
        )
      );
  }
  return checkResponse(response);
}

// @ts-expect-error TS7006 Adopt TS
export function responseToJson(response) {
  return checkResponse(response) || response.json();
}

// @ts-expect-error TS7006 Adopt TS
export function fetchJson(url, options = {}) {
  return fetch(url, {
    ...options,
    headers: {
      Accept: "application/json",
    },
    credentials: "include",
  }).then(responseToJson);
}

// @ts-expect-error TS7006 Adopt TS
export function createFormBody(obj) {
  return Object.keys(obj)
    .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`)
    .join("&");
}

// @ts-expect-error TS7006 Adopt TS
export function postForm(url, obj) {
  return fetch(url, {
    method: "POST",
    redirect: "follow",
    body: createFormBody(obj),
    headers: {
      "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
    },
  });
}

/**
 * Sends a compressed file as form data to a specified url
 * @param {string} url - required target url
 * @param {File} file
 * @param {Object} options - optional param for configurations
 * - urlMethod = string, HTTP method type for target url
 * - handleError, handleUpdate, handleSuccess = callback functions
 * - additionalFormFields = object, additional form params
 * - fileFieldName = string, for file input field name
 */
// @ts-expect-error TS7006 Adopt TS
export function uploadSingleFileForm(url, file, options = {}) {
  const fileReader = new FileReader();
  const {
    // @ts-expect-error TS2339 Adopt TS
    urlMethod = "POST",
    // @ts-expect-error TS2339 Adopt TS
    // biome-ignore lint/suspicious/noShadow: Biome linting adoption
    handleError = _.noop,
    // @ts-expect-error TS2339 Adopt TS
    handleUpdate = _.noop,
    // @ts-expect-error TS2339 Adopt TS
    handleSuccess = _.noop,
    // @ts-expect-error TS2339 Adopt TS
    additionalFormFields = {},
    // @ts-expect-error TS2339 Adopt TS
    fileFieldName = "file",
  } = options;
  fileReader.onprogress = handleUpdate;
  fileReader.onload = (event) => {
    const compressedFile = new Blob([
      // @ts-expect-error TS18047 Adopt TS
      gzip(event.currentTarget.result, { level: 1 }),
    ]);
    const fd = new FormData();
    Object.entries(additionalFormFields).forEach(([key, value]) => {
      // @ts-expect-error TS18046 Adopt TS
      fd.append(key, value.toString());
    });
    fd.append(fileFieldName, compressedFile, `${file.name}.gz`);
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener("progress", handleUpdate, false);
    xhr.addEventListener("load", handleSuccess, false);
    xhr.addEventListener("error", handleError, false);
    xhr.open(urlMethod, url, true);
    xhr.send(fd);
  };
  fileReader.readAsArrayBuffer(file);
}

/*
 * Reads a file from a File object.
 * @param {File} file
 * @returns {Promise} A Promise of the results of `FileReader.readAsArrayBuffer`
 */
// @ts-expect-error TS7006 Adopt TS
export function readFile(file) {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    // @ts-expect-error TS18047 Adopt TS
    fileReader.onload = (event) => resolve(event.currentTarget.result);
    fileReader.onerror = reject;
    fileReader.readAsArrayBuffer(file);
  });
}

/**
 * Packs arr FIFO evenly into n arrays/buckets based on given value. Buckets are filled based on the lowest
 * respective running total and then left to right.
 * @param {Object[]} arr - List of elements to run getValue() on.
 * @param {number} size - Number of buckets to fill.
 * @param {Function} getValue - Ran on each element of arr. Must return a numerical value.
 * @example
 * packBy([4, 3, 2, 1], 2) // returns [[4, 1], [3, 2]]
 * packBy([4, 3, 2, 1], 3) // returns [[4], [3], [2, 1]]
 * @returns {Object[][]}
 */
// @ts-expect-error TS7006 Adopt TS
export const packBy = (arr, size = 2, getValue = (el) => el) => {
  const runningTotals = Array(size).fill(0);
  const buckets = _.groupBy(arr, (el) => {
    const minIndex = runningTotals.indexOf(_.min(runningTotals));
    runningTotals[minIndex] += getValue(el);
    return minIndex;
  });

  return Object.values(buckets);
};

// @ts-expect-error TS7006 Adopt TS
export function post(url, body) {
  return fetch(url, {
    method: "POST",
    body: JSON.stringify(body),
    headers: { "Content-Type": "application/json;charset=UTF-8" },
    credentials: "include",
  });
}

// handleError argument signature must match that of window.onerror
// @ts-expect-error TS7006 Adopt TS
function handleError(message, url, lineNumber, columnNumber, error, context) {
  // Do not log creative manager errors to skipper.
  if (window.location.pathname.includes("/creative-manager")) return;
  if (!production) {
    console.error(message, { url, lineNumber, columnNumber, error, context });
  }
  if (
    url?.startsWith("chrome-extension") ||
    error?.stack?.includes("chrome-extension")
  ) {
    return;
  }
  const msgText =
    typeof message === "string" ? message : message?.message || "Unknown error";
  if (msgText === "ResizeObserver loop limit exceeded") return;
  if (msgText?.match(/'observe' on 'IntersectionObserver'/)) return;
  if (msgText?.match(/Request unsuccessful: .* 5\d\d Server Error/)) return;
  if (msgText?.match(/^Script error\.$/)) return;
  if (
    msgText === "ResizeObserver loop completed with undelivered notifications."
  )
    return;
  if (msgText?.match(/TypeError: cancelled/)) return;

  const location = window.location.href;

  // @ts-expect-error TS7006 Adopt TS
  // biome-ignore lint/suspicious/noShadow: Biome linting adoption
  const postError = (error) =>
    post("/js_error", {
      version: appVersion,
      message: msgText,
      url,
      "line-number": lineNumber,
      "column-number": columnNumber,
      error,
      location,
      context,
    }).then((response) => {
      if (!response.ok) {
        throw new Error(
          `Post request to /js_error was not ok, got ${response.status}`
        );
      }
      return response;
    });

  if (error instanceof Error) {
    const title = `${error.name}: ${error.message}`;
    StackTrace.fromError(error)
      .then(
        (stackFrames) =>
          postError({
            stack: [
              title,
              ...stackFrames.map((sf) => `    at ${sf.toString()}`),
            ].join("\n"),
          }),
        (e) =>
          postError({
            stack: [
              title,
              "Failed to get stacktrace due to:",
              e?.stack || e?.message || JSON.stringify(e),
            ].join("\n"),
          })
      )
      .catch((e) => {
        console.error(
          "Failed to report the error:",
          message,
          url,
          lineNumber,
          columnNumber,
          error,
          context,
          "due to:",
          e
        );
      });
  } else {
    postError(JSON.stringify(error));
  }
}

// @ts-expect-error TS7006 Adopt TS
export function reportError(message, error, context) {
  // @ts-expect-error TS2345 Adopt TS
  Sentry.captureException(error, (scope) => {
    scope.setExtra("message", message);
    if (!context) return;
    Object.keys(context).forEach((k) => {
      const val = context[k];
      switch (k) {
        case "redux":
          // Don't include the Redux store, it will exceed Sentry size limitations.
          scope.setContext("reduxAction", val?.action);
          break;
        case "team":
          // Tags are searchable in Sentry.
          scope.setTag(k, val);
          break;
        default:
          scope.setContext(k, val);
      }
    });
  });
  handleError(message, undefined, undefined, undefined, error, context);
}

export async function setupWhyDidYouRender() {
  if (process.env.NODE_ENV === "development" && process.env.WDYR) {
    const { default: whyDidYouRender } = await import(
      "@welldone-software/why-did-you-render"
    );
    whyDidYouRender(React, { trackAllPureComponents: true });
  }
}

export function setupErrorHandler() {
  // @ts-expect-error TS2769 Adopt TS
  window.addEventListener("error", handleError);
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    if (reason === undefined || `${reason}` === "TypeError: Failed to fetch") {
      return;
    }
    // @ts-expect-error TS2554 Adopt TS
    reportError(`Uncaught (in promise) ${reason}`, reason);
  });
}

// @ts-expect-error TS7006 Adopt TS
export function preventDefault(f) {
  return _.isUndefined(f)
    ? undefined
    : // @ts-expect-error TS7006 Adopt TS
      (e) => {
        e.preventDefault();
        f(e);
      };
}

// @ts-expect-error TS7006 Adopt TS
export function stopPropagation(f) {
  return _.isUndefined(f)
    ? undefined
    : // @ts-expect-error TS7006 Adopt TS
      (e) => {
        e.stopPropagation();
        f(e);
      };
}

// @ts-expect-error TS7006 Adopt TS
export function just(f) {
  return _.isUndefined(f)
    ? undefined
    : // @ts-expect-error TS7006 Adopt TS
      (e) => {
        e?.preventDefault();
        e?.stopPropagation();
        f(e);
      };
}

// @ts-expect-error TS7006 Adopt TS
export function toRadians(degrees) {
  return (degrees * Math.PI) / 180;
}

// @ts-expect-error TS7006 Adopt TS
export function toDegrees(radians) {
  return (radians * 180) / Math.PI;
}

// @ts-expect-error TS7006 Adopt TS
export function defaultMenuItemDisableFunction(menuItem) {
  return {
    ...menuItem,
    disabled: menuItem.disabled || (({ data = {} } = {}) => _.isEmpty(data)),
  };
}

// @ts-expect-error TS7031 Adopt TS
export function getSpendCapVals({ daily_spend_limit, daily_revenue_limit }) {
  return daily_spend_limit
    ? { capValue: daily_spend_limit, capType: "spend" }
    : { capValue: daily_revenue_limit, capType: "revenue" };
}

// @ts-expect-error TS7006 Adopt TS
export const usesWeeklyRevenueCap = (entity) => {
  const { spend_cap_type } = entity;
  return spend_cap_type === "pacer_weekly";
};

// Translates weekly spend cap from the UI into the daily value for the database
// if appropriate for the cap type.
// @ts-expect-error TS7006 Adopt TS
export const updateSpendCapForDB = (spendCap, spendCapType) => {
  if (spendCapType === "pacer_weekly") {
    return spendCap / 7;
  }
  return spendCap;
};

// Translates the daily spend cap stored in the database into a weekly spend cap
// for campaigns on that timeframe.
// @ts-expect-error TS7006 Adopt TS
export const updateSpendCapForUI = (spendCap, spendCapType) => {
  if (spendCapType === "pacer_weekly") {
    return spendCap * 7;
  }
  return spendCap;
};

// @ts-expect-error TS7006 Adopt TS
export function metricValue(metric) {
  if (_.isPlainObject(metric)) {
    return metric.value;
  }
  return metric;
}

// @ts-expect-error TS7006 Adopt TS
export function valueOrNothing(value, formatter, nothing = "") {
  return value ? formatter(value) : nothing;
}

// @ts-expect-error TS7006 Adopt TS
export function newTab(event) {
  return event.metaKey || event.ctrlKey;
}

// @ts-expect-error TS7006 Adopt TS
export function goalKeyToMetricName(goalType) {
  const goalMetricMap = {
    nrm: "margin",
    roas_7d: "c7d_roas",
    roas_28d: "c28d_roas",
    roas_30d: "c30d_roas",
  };
  return Object.hasOwn(goalMetricMap, goalType)
    ? // @ts-expect-error TS7053 Adopt TS
      goalMetricMap[goalType]
    : goalType;
}

// @ts-expect-error TS7006 Adopt TS
export function getScheduledChangesClass(data, fieldName) {
  if (
    fieldName &&
    // @ts-expect-error TS7006 Adopt TS
    (data.scheduled_changes || []).some((c) => c.change_name === fieldName)
  ) {
    return "show-scheduled-changes";
  }
  return "";
}

// @ts-expect-error TS7006 Adopt TS
export function sortData(data, sortedData, column, descending) {
  if (isNullOrUndefined(column) || !data) {
    return data;
  }
  if (descending) {
    // biome-ignore lint/style/noParameterAssign: Biome linting adoption
    sortedData = Array.from(sortedData).reverse();
  }
  // @ts-expect-error TS7006 Adopt TS
  const { name, dataFn = (a) => a?.[name] } = column;
  // biome-ignore lint/style/noParameterAssign: Biome linting adoption
  sortedData = _.sortBy(sortedData, dataFn);
  if (descending) {
    sortedData.reverse();
  }
  return sortedData;
}

export function downloadBlob(blob: Blob, filename: string) {
  const el = document.createElement("a");
  const url = window.URL.createObjectURL(blob);
  el.href = url;
  el.style.display = "none";
  el.download = filename;
  document.body.appendChild(el);
  el.click();
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
    document.body.removeChild(el);
  });
}

/**
 * Downloads a given text as file with a given filename.
 */
export function download(filename: string, text: string) {
  const blob = new Blob([text], {
    type: "text/plain;charset:utf-8",
  });
  downloadBlob(blob, filename);
}

// @ts-expect-error TS7006 Adopt TS
export function isPromise(obj) {
  return Promise.resolve(obj) === obj;
}

/**
 * Get current window's query parameters.
 */
export function getQueryParams() {
  // biome-ignore lint/style/noSubstr: Biome linting adoption
  return new URLSearchParams(window.location.search.substr(1));
}

// @ts-expect-error TS7006 Adopt TS
export function containsIgnoreCase(str, substr) {
  return (
    typeof str === "string" &&
    typeof substr === "string" &&
    str.toLowerCase().includes(substr.toLowerCase())
  );
}

// @ts-expect-error TS7006 Adopt TS
export function equalsIgnoreCase(str, str2) {
  return (
    typeof str === "string" &&
    typeof str2 === "string" &&
    str.toLowerCase() === str2.toLowerCase()
  );
}

// @ts-expect-error TS7006 Adopt TS
export function formatUsername(user) {
  const { first_name, last_name } = user || {};
  return first_name || last_name
    ? `${first_name || ""} ${last_name[0] || ""}.`
    : "";
}

// @ts-expect-error TS7006 Adopt TS
export function sanitizeInputText(text) {
  return (
    text
      .split(/[\n,]/)
      // @ts-expect-error TS7006 Adopt TS
      .filter((inputText) => isDefined(inputText))
      // @ts-expect-error TS7006 Adopt TS
      .map((inputText) => inputText.trim())
      // @ts-expect-error TS7006 Adopt TS
      .filter((inputText) => inputText.length !== 0)
  );
}

export function refreshPage() {
  // @ts-expect-error TS2554 Adopt TS
  window.location.reload(true);
}

export const withMaxElements = (
  // @ts-expect-error TS7006 Adopt TS
  arr,
  // @ts-expect-error TS7006 Adopt TS
  max,
  ellipsis = "...",
  getValue = _.identity
) => {
  if (arr.length > max) {
    return [
      ..._.take(arr, max - 2).map(getValue),
      ellipsis,
      // @ts-expect-error TS2554 Adopt TS
      getValue(_.last(arr), max),
    ];
  }
  return arr.map(getValue);
};

// @ts-expect-error TS7006 Adopt TS
export const isValidAppId = (text) => {
  return (
    /^([A-Za-z]{1}[A-Za-z\d_]*\.)+[A-Za-z][A-Za-z\d_]*$/.test(text) ||
    /^\d{9,10}$/.test(text) ||
    // For weird stuff like "GBWhatsAppAPK"
    /^[A-Za-z]*$/.test(text)
  );
};

// Escapes literal string to be a valid regexp search string
// Taken from https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions
// @ts-expect-error TS7006 Adopt TS
export function escapeRegExp(string) {
  return string.replace(/[.*+\-?^${}()|[\]\\]/g, "\\$&"); // $& means the whole matched string
}

export const fetchUser = () =>
  fetch("/api/users/v1/me")
    .then((resp) => {
      const pathName = window.location.pathname.toUpperCase();
      const unauthorizedPathName = "/UNAUTHORIZED";
      if (resp.status === 403 && pathName !== unauthorizedPathName) {
        // @ts-expect-error TS2322 Adopt TS
        window.location = unauthorizedPathName;
      }
      return resp.json();
    })
    // FIXME(jphung) the only page that should be erroring is the password reset
    // page, remove this once the password reset page has a more permanent fix
    .catch(() => undefined);

export const fetchSiteConfig = (customer = true) => {
  return fetchJson(`/api/v2/analytics/site_config?customer=${customer}`);
};

export const PROD_ALLOW_REGEX = /^https:\/\/app.liftoff.io\/.*$/;

// NOTE(ryoung) there is some repetition in setup here and in
// skipper/client/assets/js/main.js. Given the fairly disparate JS environments
// these two code blocks should be kept in sync manually.
// Note: ignoreErrors will not be identical between skipper and dasher as these
// are project specific.
export async function initSentry(
  dsn: string,
  userPromise = fetchUser(),
  options: {
    disableReplay?: boolean;
    sampling?: { pageloadSampleRate?: number; tracesSampleRate?: number };
  } = {}
) {
  const { disableReplay = false, sampling } = options;

  initLiftoffSentry(
    {
      dsn,
      ignoreErrors: [
        "TypeError: Failed to fetch",
        "TypeError: NetworkError when attempting to fetch resource.",
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications.",
      ],
      // If the entire session is not sampled, use the below sample rate to sample
      // sessions when an error occurs.
      replaysOnErrorSampleRate: 0.5,
      allowUrls: [PROD_ALLOW_REGEX],
    },
    { getUser: userPromise, disableReplay, sampling }
  );
}

// @ts-expect-error TS7006 Adopt TS
export async function sentryCaptureException(error, extraScope = {}) {
  Sentry.withScope((scope) => {
    Object.keys(extraScope).forEach((k) => {
      // @ts-expect-error TS7053 Adopt TS
      scope.setExtra(k, extraScope[k]);
    });
    Sentry.captureException(error);
  });
}

// @ts-expect-error TS7006 Adopt TS
export const sleep = (m) => new Promise((r) => setTimeout(r, m));

// Default filter logic used by AntD select.
// @ts-expect-error TS7006 Adopt TS
export const filterOptions = (input, option) =>
  option.props.children?.toLowerCase?.().indexOf(input.toLowerCase()) >= 0;

export async function fetchVersion() {
  /* global __webpack_public_path__:writable */
  return (await fetch(`${__webpack_public_path__}version`)).text();
}

// @ts-expect-error TS7006 Adopt TS
export const isEmail = (email) => {
  // https://stackoverflow.com/a/26272713
  // https://html.spec.whatwg.org/multipage/input.html#email-state-(type=email)
  return /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(
    email
  );
};
